import { Flex, SkeletonBox, Text } from '@billy/design-system'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { useFetchQuote } from '@features/invoices/hooks/useFetchQuote'

import { formatAmountCurrency } from '../../../../../../../../utils/stringFormatter'
import { InvoiceEditorUrlParams } from '../../../../../../types/invoiceEditorUrlParams'
import { invoiceDraftEditFromQuoteVariants } from '../../../../constants/variants/invoiceDraftEditFromQuoteVariants'
import { useFetchSalesDocumentDraft } from '../../../../hooks/useFetchSalesDocumentDraft'
import { useInvoiceEditorDocumentType } from '../../../../hooks/useInvoiceEditorDocumentType'
import { useInvoiceEditorVariant } from '../../../../hooks/useInvoiceEditorVariant'
import { getQuoteIdFromDraft } from '../../../../utils/invoiceDraft/getQuoteIdFromDraft'

export const RelatedDocumentSubtitle = (): ReactElement | null => {
  const { t } = useTranslation()
  const { documentId } = useParams<InvoiceEditorUrlParams>()
  const { documentType } = useInvoiceEditorDocumentType()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()

  const isOperatingOnDraftFromQuote = invoiceDraftEditFromQuoteVariants.includes(invoiceEditorVariant)

  const { salesDocumentDraft, isLoading: isFetchingDraft } = useFetchSalesDocumentDraft(
    documentType,
    isOperatingOnDraftFromQuote ? documentId : undefined,
  )

  const quoteId = isOperatingOnDraftFromQuote ? getQuoteIdFromDraft(salesDocumentDraft) : documentId

  const { quote, isLoading: isFetchingQuote } = useFetchQuote(quoteId)
  const isLoading = isFetchingDraft || isFetchingQuote

  if (isLoading) {
    return <SkeletonBox height={20} width={240} />
  }

  if (!quote) {
    return null
  }

  const formattedAmount = formatAmountCurrency(quote.summary.foreignNetTotal, quote.summary.foreignCurrency)

  const subtitle = quote.estimateNumber
    ? t('external_invoices.editor.related_document_subtitle.quote_with_number', {
        quoteNumber: quote.estimateNumber,
        amount: formattedAmount,
      })
    : t('external_invoices.editor.related_document_subtitle.quote_without_number', { amount: formattedAmount })

  return (
    <Flex alignItems="center">
      <Text variant="bodyMD">{subtitle}</Text>
    </Flex>
  )
}
